'use client';

import React, { useEffect } from 'react';
import { usePathname, useRouter } from 'next/navigation';
import { useAuthStore } from '@/app/model/stores';
import { MobileNavigation } from '@/app/ui/MobileNavigation';
import { SubscriptionNotifications } from '@/features/subscription';
import { GlobalLoadingBar } from '@/shared/ui';
import { useIsLoading } from '@/shared/loading';
import { useLayoutConfig } from '@/features/design/concept/hooks/use-layout-config';
import { KeyboardShortcutsHelp } from '@/shared/ui';
import { useJobQueue } from '@/features/design/job-queue';
import { OnboardingAutoStart } from '@/features/support/ui/OnboardingAutoStart';
import { LoadingSpinner } from '@/shared/ui';
import { NewProjectDialog, ActiveProjectSync } from '@/features/project';
import { cn } from '@/shared/lib/cn';

// Force dynamic rendering for all authenticated pages
export const dynamic = 'force-dynamic';

export default function ProtectedLayout({
  children,
}: {
  children: React.ReactNode;
}) {
  const pathname = usePathname();
  const router = useRouter();

  // Check if current page is history page
  const isHistoryPage =
    pathname === '/history' || pathname.startsWith('/history/');

  // Check if current page is the help center (top-level, reachable from the
  // main sidebar — no longer an account sub-page).
  const isHelpPage = pathname === '/help' || pathname.startsWith('/help/');

  // Check if current page is account page (hub or subpage)
  const isAccountPage =
    pathname === '/account' || pathname.startsWith('/account/');

  // Pages that use full-height desktop layout with sidebar (h-dvh at lg+)
  const hasFullHeightDesktopLayout =
    pathname.startsWith('/project/') ||
    isHistoryPage ||
    isHelpPage ||
    isAccountPage;

  // Auth state from store
  const loading = useAuthStore(
    (state) => state.isLoggingIn || state.isRegistering || state.isLoggingOut
  );
  const isInitialized = useAuthStore((state) => state.isInitialized);
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const pendingEmailVerification = useAuthStore(
    (state) => state.pendingEmailVerification
  );

  const isGlobalLoading = useIsLoading();

  // Use the new hook that combines pathname-based config with store data
  const layoutConfig = useLayoutConfig();

  // Initialize job queue - this will only fetch if the queue is empty
  // WebSocket provider handles initial sync on connection, but we need this
  // for cases where the component mounts after WebSocket is already connected
  useJobQueue(10);

  // Auth gating is fully client-side (mobile parity); these effects redirect
  // unauthenticated/unverified users via the App Router (no full reload).
  const needsEmailVerification =
    isInitialized && isAuthenticated && pendingEmailVerification;
  const needsLogin = isInitialized && !isAuthenticated;

  useEffect(() => {
    if (needsEmailVerification) {
      router.replace('/auth/verify-email');
    } else if (needsLogin) {
      const redirectTarget =
        pathname && pathname !== '/'
          ? `?redirect=${encodeURIComponent(pathname)}`
          : '';
      router.replace(`/auth/login${redirectTarget}`);
    }
  }, [needsEmailVerification, needsLogin, pathname, router]);

  // Show a centered spinner during auth init / login / logout — store hydrates
  // post-mount, so the route segment's loading.tsx is already past by this point.
  if (!isInitialized || loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  // While the redirect effect runs we render nothing — keeps protected layout
  // out of the DOM for unauthenticated/unverified users.
  if (needsEmailVerification || needsLogin) {
    return null;
  }

  return (
    <div
      className={cn(
        'bg-panel min-h-screen',
        hasFullHeightDesktopLayout && 'desktop-full-height-layout'
      )}
    >
      <GlobalLoadingBar isLoading={isGlobalLoading} />

      {/* Single always-mounted owner of the persisted active building. Lives
          here (not in a nav) because the desktop sidebar only mounts on
          wide+expanded screens, so it could not reliably persist the choice. */}
      <ActiveProjectSync />

      <SubscriptionNotifications />

      {/* Main content with proper padding */}
      <main
        className={cn(
          'pb-20 lg:pb-0',
          hasFullHeightDesktopLayout && 'desktop-full-height-main'
        )}
      >
        {/* Page content */}
        {children}
      </main>

      {/* Bottom Navigation - floating island, visible below lg when sidebar is hidden */}
      {!layoutConfig.hideMobileNavigation && <MobileNavigation />}

      {/* Shared create-building dialog — opened from the project header ⋮ menu,
          the project switcher list and the no-buildings empty state. */}
      <NewProjectDialog />

      {/* Keyboard Shortcuts Help */}
      <KeyboardShortcutsHelp />

      {/* First-launch onboarding carousel — renders itself only when the
          per-device flag is not set. No-op on subsequent sessions. */}
      <OnboardingAutoStart />
    </div>
  );
}
